import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { Card, Typography, Row, Col, Empty, Spin, Button } from 'antd';
import { useNavigate } from 'react-router-dom';
import { FileTextOutlined, BookOutlined } from '@ant-design/icons';
import styles from './TikuCategory.module.css';
import axios from 'axios';
import { message } from 'antd';

const { Title, Text } = Typography;

const TikuCategory = () => {
  const { category, subCategory } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [examList, setExamList] = useState([]);
  const [categoryInfo, setCategoryInfo] = useState(null);

  const fetchData = async () => {
    try {
      setLoading(true);
      const collection = collectionMap[category] || 'economist_exam';
      
      const response = await axios.get(`/api/exam/${collection}/list`, {
        params: {
          category,
          subCategory
        }
      });

      if (response.data.success) {
        setCategoryInfo(response.data.category);
        setExamList(response.data.exams);
      } else {
        throw new Error(response.data.message || '获取数据失败');
      }
    } catch (error) {
      console.error('获取数据失败:', error);
      message.error(error.message || '加载失败');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, [category, subCategory]);

  // 定义集合映射关系
  const collectionMap = {
    'accounting': 'accounting_exam',
    'economist': 'economist_exam',
    'construction': 'construction_exam',
    'teacher': 'teacher_exam'
  };

  const handleExamClick = (exam) => {
    // 获取当前分类对应的集合名称
    const collection = collectionMap[category] || 'accounting_exam';
    
    // 构造导航状态
    const navigationState = {
      examInfo: {
        exam_data: exam.exam_data,
        category: exam.category,
        collection: collection // 使用动态确定的集合名称
      },
      collection: collection
    };

    // 导航到练习页面，添加examCategory参数
    navigate(`/exam/practice/${collection}/${encodeURIComponent(exam.exam_data)}?examCategory=${encodeURIComponent(subCategory)}`, {
      state: navigationState
    });
  };

  // 添加分类处理函数
  const categorizeExams = (exams) => {
    const yearExams = [];
    const mockExams = [];
    const practiceExams = [
      { 
        _id: 'single',
        name: '单选题',
        exam_data: '单选题',
        questionCount: 120, // 这里可以从后端获取实际数量
        type: 'single'
      },
      {
        _id: 'multiple',
        name: '多选题',
        exam_data: '多选题',
        questionCount: 80,
        type: 'multiple'
      },
      {
        _id: 'judge',
        name: '判断题',
        exam_data: '判断题',
        questionCount: 50,
        type: 'judge'
      },
      {
        _id: 'essay',
        name: '问答题',
        exam_data: '问答题',
        questionCount: 30,
        type: 'essay'
      }
    ];

    exams.forEach(exam => {
      if (exam.exam_data.includes('模拟')) {
        mockExams.push(exam);
      } else {
        // 使用正则表达式匹配包含年份的试题
        const hasYear = /\d{4}年/.test(exam.exam_data);
        if (hasYear) {
          yearExams.push(exam);
        } else {
          // 如果既不包含年份也不包含"模拟"，默认放入年份类
          yearExams.push(exam);
        }
      }
    });

    // 按年份降序排序
    yearExams.sort((a, b) => {
      const yearA = a.exam_data.match(/\d{4}/)?.[0] || '0';
      const yearB = b.exam_data.match(/\d{4}/)?.[0] || '0';
      return yearB - yearA;
    });

    return { yearExams, mockExams, practiceExams };
  };

  if (loading) {
    return (
      <div className={styles.loadingContainer}>
        <Spin size="large" />
      </div>
    );
  }

  return (
    <div className={styles.categoryContainer}>
      <div className={styles.header}>
        <div className={styles.breadcrumbContainer}>
          <div className={styles.breadcrumb}>
            <Text 
              className={styles.parentCategory}
              onClick={() => {
                // 清除上次选择，返回主分类页面
                localStorage.removeItem('lastSelectedCategory');
                
                // 发布一个自定义事件，通知其他组件默认分类已重置
                const event = new CustomEvent('categoryChanged', { detail: null });
                window.dispatchEvent(event);
                
                navigate('/tiku');
              }}
            >
              ⟳ 重置专业
            </Text>
            <Text className={styles.separator}>/</Text>
            <Text 
              className={styles.parentCategory}
              onClick={() => navigate('/tiku')}
            >
              {category === 'accounting' ? '会计专业' : 
               category === 'economics' ? '经济专业' :
               category === 'construction' ? '建工专业' :
               category === 'computer' ? '计算机软考' :
               category === 'teacher' ? '教师资格' : '专业分类'}
            </Text>
            <Text className={styles.separator}>/</Text>
            <Text className={styles.currentCategory}>
              {examList.length > 0 ? examList[0].category : categoryInfo?.name}
            </Text>
          </div>
          <div className={styles.headerActions}>
            <Text className={styles.examCount}>
              共{examList.length}套试题
            </Text>
            <Button
              icon={<BookOutlined />}
              className={styles.knowledgeButton}
              onClick={() => window.open(`/study-book/${subCategory || ''}`, '_blank')}
            >
              进入知识笔记
            </Button>
          </div>
        </div>
      </div>

      {examList.length === 0 ? (
        <div className={styles.emptyContainer}>
          <Empty description="暂无试题集" />
        </div>
      ) : (
        <div>
          {/* 真题部分 */}
          {categorizeExams(examList).yearExams.length > 0 && (
            <>
              <Title level={3} className={styles.sectionTitle}>历年真题</Title>
              <div className={styles.examCardContainer}>
                {categorizeExams(examList).yearExams.map((exam, index) => (
                  <div
                    key={exam._id}
                    className={styles.examCard}
                    onClick={() => handleExamClick(exam)}
                  >
                    <Title level={5} className={styles.cardTitle}>
                      {exam.name || exam.exam_data}
                    </Title>
                    <div className={styles.questionCount}>
                      题目数量: {exam.questionCount || exam.count || 0}
                    </div>
                  </div>
                ))}
              </div>
            </>
          )}

          {/* 模拟试题部分 */}
          {categorizeExams(examList).mockExams.length > 0 && (
            <>
              <Title level={3} className={`${styles.sectionTitle} ${styles.mockTitle}`}>模拟试题</Title>
              <div className={styles.examCardContainer}>
                {categorizeExams(examList).mockExams.map((exam, index) => (
                  <div
                    key={exam._id}
                    className={styles.examCard}
                    onClick={() => handleExamClick(exam)}
                  >
                    <Title level={5} className={styles.cardTitle}>
                      {exam.name || exam.exam_data}
                    </Title>
                    <div className={styles.questionCount}>
                      题目数量: {exam.questionCount || exam.count || 0}
                    </div>
                  </div>
                ))}
              </div>
            </>
          )}

          {/* 添加机选试题部分 */}
          {categorizeExams(examList).practiceExams.length > 0 && (
            <>
              <Title level={3} className={`${styles.sectionTitle} ${styles.practiceTitle}`}>
                机选试题
              </Title>
              <div className={styles.examCardContainer}>
                {categorizeExams(examList).practiceExams.map((exam) => (
                  <div
                    key={exam._id}
                    className={`${styles.examCard} ${styles.practiceCard}`}
                    onClick={() => handleExamClick(exam)}
                  >
                    <Title level={5} className={styles.cardTitle}>
                      {exam.name}
                    </Title>
                    <div className={styles.questionCount}>
                      题目数量: {exam.questionCount}
                    </div>
                  </div>
                ))}
              </div>
            </>
          )}
        </div>
      )}
    </div>
  );
};

export default TikuCategory; 